import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { ProductCard } from "../components/ProductCard";
import { Star, ExternalLink, ArrowLeft, ShieldCheck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        return api.get(`/products?category=${r.data.category_slug}`);
      })
      .then((r) => setRelated(r.data.filter((p) => p.id !== id).slice(0, 4)))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="mx-auto max-w-7xl px-6 py-32 text-center min-h-[60vh]">
        <h1 className="font-display text-3xl font-bold mb-4">Product not found</h1>
        <Link to="/" className="text-[#FF2A00] underline">Back home</Link>
      </div>
    );

  if (!product) return <div className="mx-auto max-w-7xl px-6 py-32 text-zinc-400 min-h-[60vh]">Loading…</div>;

  return (
    <div data-testid="product-detail-page">
      <div className="mx-auto max-w-7xl px-6 md:px-12 pt-8">
        <Link to={`/category/${product.category_slug}`} data-testid="back-to-category" className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-black transition-colors">
          <ArrowLeft size={16} /> Back to {product.category_slug}
        </Link>
      </div>

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-10 grid md:grid-cols-2 gap-12 items-start">
        <div className="border border-zinc-200 overflow-hidden bg-zinc-100">
          <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
        </div>

        <div className="md:py-4">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-4">{product.category_slug}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-none mb-5">{product.title}</h1>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center gap-1 text-[#FF2A00]">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={16} fill={i < Math.round(product.rating) ? "currentColor" : "none"} strokeWidth={1.5} />
              ))}
            </div>
            <span className="text-sm font-semibold">{product.rating?.toFixed(1)}</span>
          </div>

          <p className="text-zinc-700 leading-relaxed mb-8">{product.description}</p>

          <div className="flex items-baseline gap-3 mb-8">
            <span className="font-display text-4xl font-black">{product.price}</span>
            <span className="text-sm text-zinc-400">on Amazon</span>
          </div>

          <a
            href={product.amazon_url}
            target="_blank"
            rel="noopener noreferrer sponsored"
            data-testid="amazon-outbound-link"
            className="inline-flex items-center justify-center gap-2 bg-[#FF2A00] hover:bg-[#D92400] text-white px-8 py-4 font-medium text-base w-full sm:w-auto transition-colors duration-300"
          >
            View on Amazon <ExternalLink size={18} />
          </a>

          <div className="flex items-center gap-2 mt-5 text-sm text-zinc-500">
            <ShieldCheck size={16} className="text-zinc-400" />
            As an Amazon Associate we earn from qualifying purchases.
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="bg-[#F4F4F5] py-16 mt-8">
          <div className="mx-auto max-w-7xl px-6 md:px-12">
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight font-medium mb-8">More in {product.category_slug}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
